/**
 * Authentication Context
 *
 * Manages authentication state for the application.
 * Features:
 * - Session management with persistent storage
 * - User identity with proper typing (EndUser vs ITStaffUser)
 * - Role-based access helpers
 * - Simulated login (ready for real auth provider integration)
 *
 * Architecture:
 * - Uses localStorage for session persistence (will be replaced by auth provider)
 * - Validates session on mount
 * - Provides type-safe user access
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  type ReactNode,
} from 'react';
import type {
  AuthState,
  AuthenticatedUser,
  ITStaffUser,
  EndUser,
} from '../types/auth';
import {
  INITIAL_AUTH_STATE,
  UserType,
  isITStaff,
  isEndUser,
  canAccessITPortal,
  getUserIdForTicket,
  getTechIdForAssignment,
} from '../types/auth';
import { UserRole, TechTier, Region, TechWorkStatus, Location, JobTag } from '../types/ticket';
import { Department } from '../types/knowledgeBase';

// ============================================
// Context Types
// ============================================

interface AuthContextValue extends AuthState {
  /** Log in as a user (simulated - will be replaced by real auth) */
  login: (userId: string) => Promise<void>;
  /** Log out and clear session */
  logout: () => void;
  /** Switch between demo users (development only) */
  switchUser: (userId: string) => Promise<void>;
  /** Check if user can access IT portal */
  hasITAccess: boolean;
  /** Get user ID for ticket fields */
  getUserId: () => string;
  /** Get tech ID for assignment (null for non-IT users) */
  getTechId: () => string | null;
}

interface AuthProviderProps {
  children: ReactNode;
}

// ============================================
// Constants
// ============================================

const AUTH_STORAGE_KEY = 'percepta-auth-session';
const SESSION_EXPIRY_HOURS = 8;

// ============================================
// Demo Users (will be replaced by real auth)
// ============================================

/**
 * Demo users for development and testing.
 * In production, these would come from your auth provider (AWS Cognito, etc.)
 */
const DEMO_USERS: Record<string, AuthenticatedUser> = {
  // IT Staff Users
  'tech-daryn': {
    id: 'user-daryn',
    userType: UserType.IT_STAFF,
    email: 'daryn@percepta.com',
    name: 'Daryn Paterson',
    department: Department.IT,
    location: Location.US_HOUSTON,
    employeeId: 'EMP001',
    cdsid: 'dpaterson',
    role: UserRole.T2_TECH,
    techId: 'tech-daryn',
    tier: TechTier.T2,
    region: Region.US,
    skills: [
      JobTag.SOFTWARE,
      JobTag.NETWORKING,
      JobTag.VPN,
      JobTag.O365,
      JobTag.TEAMS,
    ],
    currentLoad: 0,
    maxLoad: 8,
    isAvailable: true,
    workStatus: TechWorkStatus.AVAILABLE,
  } as ITStaffUser,

  'tech-sarah': {
    id: 'user-sarah',
    userType: UserType.IT_STAFF,
    email: 'sarah.chen@percepta.com',
    name: 'Sarah Chen',
    department: Department.IT,
    location: Location.US_DEARBORN,
    employeeId: 'EMP002',
    cdsid: 'schen',
    role: UserRole.T1_TECH,
    techId: 'tech-sarah',
    tier: TechTier.T1,
    region: Region.US,
    skills: [JobTag.HARDWARE, JobTag.LAPTOP, JobTag.DESKTOP, JobTag.MONITOR],
    currentLoad: 3,
    maxLoad: 10,
    isAvailable: true,
    workStatus: TechWorkStatus.WORKING,
  } as ITStaffUser,

  'tech-admin': {
    id: 'user-admin',
    userType: UserType.IT_STAFF,
    email: 'admin@percepta.com',
    name: 'IT Admin',
    department: Department.IT,
    location: Location.US_DEARBORN,
    employeeId: 'EMP000',
    cdsid: 'itadmin',
    role: UserRole.ADMIN,
    techId: 'tech-admin',
    tier: TechTier.T2,
    region: Region.US,
    skills: Object.values(JobTag),
    currentLoad: 0,
    maxLoad: 5,
    isAvailable: true,
    workStatus: TechWorkStatus.AVAILABLE,
  } as ITStaffUser,

  // End Users (non-IT)
  'user-john': {
    id: 'user-john',
    userType: UserType.END_USER,
    email: 'john.smith@percepta.com',
    name: 'John Smith',
    department: Department.OPERATIONS,
    location: Location.US_HOUSTON,
    employeeId: 'EMP100',
    cdsid: 'jsmith',
  } as EndUser,

  'user-maria': {
    id: 'user-maria',
    userType: UserType.END_USER,
    email: 'maria.garcia@percepta.com',
    name: 'Maria Garcia',
    department: Department.FINANCE,
    location: Location.US_DEARBORN,
    employeeId: 'EMP101',
    cdsid: 'mgarcia',
  } as EndUser,
};

// Default user for demo (IT staff with full access)
const DEFAULT_USER_ID = 'tech-daryn';

// ============================================
// Session Storage Helpers
// ============================================

interface StoredSession {
  userId: string;
  expiresAt: number;
}

function saveSession(userId: string): void {
  const session: StoredSession = {
    userId,
    expiresAt: Date.now() + SESSION_EXPIRY_HOURS * 60 * 60 * 1000,
  };
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // localStorage not available or quota exceeded
    console.warn('Failed to save auth session to localStorage');
  }
}

function loadSession(): StoredSession | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return null;

    const session: StoredSession = JSON.parse(stored);

    // Check if session is expired
    if (session.expiresAt < Date.now()) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }

    return session;
  } catch {
    return null;
  }
}

function clearSession(): void {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // Ignore errors
  }
}

// ============================================
// Context
// ============================================

const AuthContext = createContext<AuthContextValue | null>(null);

// ============================================
// Provider
// ============================================

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(INITIAL_AUTH_STATE);

  /**
   * Initialize auth on mount - check for existing session.
   */
  useEffect(() => {
    const initializeAuth = async () => {
      const session = loadSession();

      if (session && DEMO_USERS[session.userId]) {
        // Valid session found
        setState({
          isInitialized: true,
          isAuthenticated: true,
          isLoading: false,
          user: DEMO_USERS[session.userId],
          error: null,
        });
      } else {
        // No valid session - auto-login as default user for demo
        // In production, this would redirect to login page
        const defaultUser = DEMO_USERS[DEFAULT_USER_ID];
        if (defaultUser) {
          saveSession(DEFAULT_USER_ID);
          setState({
            isInitialized: true,
            isAuthenticated: true,
            isLoading: false,
            user: defaultUser,
            error: null,
          });
        } else {
          setState({
            isInitialized: true,
            isAuthenticated: false,
            isLoading: false,
            user: null,
            error: null,
          });
        }
      }
    };

    initializeAuth();
  }, []);

  /**
   * Log in as a user.
   * In production, this would call your auth provider (AWS Cognito, etc.)
   */
  const login = useCallback(async (userId: string): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    // Simulate auth delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    const user = DEMO_USERS[userId];
    if (!user) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'Invalid user credentials',
      }));
      return;
    }

    saveSession(userId);
    setState({
      isInitialized: true,
      isAuthenticated: true,
      isLoading: false,
      user,
      error: null,
    });
  }, []);

  /**
   * Log out and clear session.
   */
  const logout = useCallback(() => {
    clearSession();
    setState({
      isInitialized: true,
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null,
    });
  }, []);

  /**
   * Switch to a different demo user (development only).
   */
  const switchUser = useCallback(
    async (userId: string): Promise<void> => {
      await login(userId);
    },
    [login]
  );

  /**
   * Get user ID for ticket fields (requester, creator).
   */
  const getUserId = useCallback((): string => {
    if (!state.user) {
      throw new Error('No authenticated user');
    }
    return getUserIdForTicket(state.user);
  }, [state.user]);

  /**
   * Get tech ID for assignment fields (null for non-IT users).
   */
  const getTechId = useCallback((): string | null => {
    if (!state.user) return null;
    return getTechIdForAssignment(state.user);
  }, [state.user]);

  /**
   * Whether current user has IT portal access.
   */
  const hasITAccess = useMemo(
    () => canAccessITPortal(state.user),
    [state.user]
  );

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
      switchUser,
      hasITAccess,
      getUserId,
      getTechId,
    }),
    [state, login, logout, switchUser, hasITAccess, getUserId, getTechId]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

// ============================================
// Hooks
// ============================================

/**
 * Hook to access auth context.
 * @throws Error if used outside of AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to get current authenticated user.
 * @throws Error if no user is authenticated
 */
export function useAuthenticatedUser(): AuthenticatedUser {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated || !user) {
    throw new Error('User is not authenticated');
  }
  return user;
}

/**
 * Hook to get current IT staff user.
 * @throws Error if user is not IT staff
 */
export function useITStaffUser(): ITStaffUser {
  const user = useAuthenticatedUser();
  if (!isITStaff(user)) {
    throw new Error('User is not IT staff');
  }
  return user;
}

/**
 * Hook to safely get user if authenticated (returns null if not).
 */
export function useOptionalUser(): AuthenticatedUser | null {
  const context = useContext(AuthContext);
  return context?.user ?? null;
}

/**
 * Get available demo users for user switcher (development only).
 */
export function getDemoUsers(): { id: string; user: AuthenticatedUser }[] {
  return Object.entries(DEMO_USERS).map(([id, user]) => ({ id, user }));
}

// Re-export type guards for convenience
export { isITStaff, isEndUser, isAdmin, isT2Tech } from '../types/auth';
